import React, { useEffect, useMemo, useState } from "react";
import HeaderContainer from "../../components/container/HeaderContainer";
import Backlink from "../../components/Backlink";
import { useUser } from "../../context/UserContext";
import { useMutation } from "@tanstack/react-query";
import CustomizedModal from "../../components/UI/CustomizedModal";
import { dataImport } from "../../services/AdminServices";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import DisplayTable from "../../components/tables/DisplayTable";
import { ImportFilesBaseStructure } from "../../components/tools/importFiles/ImportFilesBaseStructure";
import { ImportFilesConfirmation } from "../../components/tools/importFiles/ImportFilesConfirmation";
import { FaCheckCircle } from "react-icons/fa";
import { tableColumnWrap } from "../../components/tables/tableStyles";
const Sample_Results_File = "/SampleResultsImport_Template.csv";

export const ImportKits = () => {
  const { userInfo } = useUser();
  const { user_email } = userInfo;
  const [customModal, setCustomModal] = useState();
  const [inputFile, setInputFile] = useState({
    name: "",
  });
  const [formData, setFormData] = useState(null);

  const [response, setResponse] = useState({});

  const mutation = useMutation({
    mutationFn: dataImport,
    onError: (error) => {
      setCustomModal({
        title: "Error Occured",
        message: error?.response?.data?.error_details || error?.response?.data?.message,
      });
    },
  });

  useEffect(() => {
    if (mutation?.data?.data) {
      setResponse(mutation?.data?.data);
    }
  }, [mutation?.data?.data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "row_number",
        header: "row_number",
        size: 100
      },
      {
        accessorKey: "ps_code",
        header: "ps_code",
        size: 100
      },
      {
        accessorKey: "visit_date",
        header: "visit_date",
        size: 100
      },
      {
        accessorKey: "sample_kit",
        header: "sample_kit",
        size: 100
      },
      {
        accessorKey: "error",
        header: "error",
        size: 100,
        Cell: ({ cell }) => {
          const error = cell.getValue();
          if (error === null) return;
          return <div style={tableColumnWrap()}>{error}</div>;
        },
      }
    ],
    [response]
  );

  const insertKits = () => {
    setFormData(prev => {
      if (prev instanceof FormData) {
        const form = prev;
        form.append("import_type", "sample_kits");
        form.append("import_by", user_email);
        form.append("commit", true);
        return form;
      }
      return prev;
    });

    mutation.mutate(formData);
  };

  return (
    <>
      {customModal && (
        <CustomizedModal
          title={customModal.title}
          message={customModal.message}
          onCancel={() => setCustomModal(null)}
        />
      )}
      <section className="w-full text-primary">
        <Backlink>Back</Backlink>
        <section className="my-8 text-primary w-full">
          <HeaderContainer title="Sample Kits Import">
            <ImportFilesBaseStructure
              title={"Upload the Sample Kits CSV file to insert sample kits."}
              sampleFileInfo={
                "File must be comma-separated, and first row must contain only these fields in this exact order: Global_Id, pwsid, ps_code, visit_date, sample_kit."
              }
              sampleFilePath={Sample_Results_File}
              sampleFileText={"Sample Kits Import"}
              inputFile={inputFile}
              import_type={"sample_kits"}
              setInputFile={setInputFile}
              setFormData={setFormData}
              setResponse={setResponse}
              mutation={mutation}
            />

            {/* Inserted Rows */}
            {inputFile?.name &&
              response?.imported_records > 0 && (
                <>
                  <p className="mt-5 italic flex">
                    <FaCheckCircle className="text-green-500 w-[20px] h-[20px] mr-2" />
                    {response?.imported_records} of{" "}
                    {response?.total_records} records
                    inserted
                  </p>
                </>
              )
            }

            {/* Confirmation */}
            {inputFile?.name &&
              response?.valid_records > 0 &&
              !mutation.isError &&
              response?.imported_records === 0 && (
                <ImportFilesConfirmation
                  total_rows={response?.valid_records}
                  confirmation_text={
                    ` valid records out of ${response?.total_records} are ready to be updated. Would you like to proceed with the update?`
                  }
                  onConfirm={insertKits}
                  onCancel={() => {
                    setInputFile({ name: "" });
                    setResponse({});
                  }}
                />
              )
            }

            {mutation?.isPending ? (
              <LoadingSpinner size="30px" enableLoadingText={true} />
            ) : (
              <>

                {/* invalid Rows */}
                {inputFile.name && response?.invalid_records?.length > 0 && (
                  <>
                    <p className="mt-5 pb-2">
                      {response?.invalid_records.length} records found with errors
                    </p>
                    <DisplayTable
                      tableData={
                        response?.invalid_records
                      }
                      columns={columns}
                      showExportButton={true}
                    />
                  </>
                )}

                {/* No updates needed */}
                {inputFile.name && response?.valid_records === 0 && response?.invalid_records?.length === 0 && (
                  <p className="mt-5 italic flex">
                    <FaCheckCircle className="text-green-500 w-[20px] h-[20px] mr-2" />
                    No updates needed. All records are already up to date.
                  </p>
                )}

              </>
            )}
          </HeaderContainer>
        </section>
      </section>
    </>
  );
};


export default ImportKits;